"use client";
import { useEffect, useRef, useState, type ComponentProps } from "react";

export type TableOverflow = "none" | "start" | "end" | "both";

/** Which edges of a horizontally scrolling table still hide columns. */
export function tableOverflow(scrollLeft: number, scrollWidth: number, clientWidth: number): TableOverflow {
  const hidden = scrollWidth - clientWidth;
  if (hidden <= 1) return "none";
  const before = scrollLeft > 1;
  const after = scrollLeft < hidden - 1;
  return before && after ? "both" : before ? "start" : "end";
}

/*
 * A wide table scrolls inside the response. Its scrollbar is the app's quiet
 * one (scrollbar-thin), shown only under the pointer or keyboard focus, and it
 * scrolls sideways only, so it keeps no gutter for a vertical bar. The edge
 * that still hides columns fades out (typeset-scroll[data-overflow] in
 * globals.css) to show there is more.
 */
export function ScrollableTable(props: ComponentProps<"table">) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<TableOverflow>("none");
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () =>
      setOverflow(tableOverflow(element.scrollLeft, element.scrollWidth, element.clientWidth));
    update();
    element.addEventListener("scroll", update, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(element);
    if (element.firstElementChild) observer?.observe(element.firstElementChild);
    return () => {
      element.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, []);
  return (
    <div ref={ref} className="typeset-scroll scrollbar-thin [scrollbar-gutter:auto]" role="region" aria-label="Table" tabIndex={0} data-overflow={overflow}>
      <table {...props} />
    </div>
  );
}
